// Frozen vocabulary + payload-shape contract for the app store's intent bus.
// Each entry in INTENTS maps to an entry in IntentPayloadMap; together they
// let `store.dispatch(name, payload)` and `store.onIntent(name, fn)` enforce
// at compile time that handlers and dispatchers agree on payload shape.
//
// New intent names go here AND in the appropriate handler module (typically
// src/app/intents.ts in PR 8).

import type { CameraPreset, CameraProjection, ColorbarUnitsMode, ColormapBinding, ColormapBindingId, NavMode, PrimitiveKey, Reduction, ReductionId, SelectionId, Vec3Obj } from './schema';

export const INTENTS = {
  // === selection ===
  MOVE_SELECTION: 'moveSelection',
  SET_SELECTION_POSITION: 'setSelectionPosition',
  SET_SELECTION_SIZE: 'setSelectionSize',
  SET_SELECTION_VISIBILITY: 'setSelectionVisibility',
  // Center-dot + projection-line overlay (distinct from the cube above).
  SET_SELECTION_OVERLAY_VISIBILITY: 'setSelectionOverlayVisibility',
  // A tap (click without drag) on the probe cube — distinct from the
  // continuous MOVE_SELECTION stream a drag emits. Toggles the Inspector.
  SELECTION_PROBE_CLICK: 'selection:probe-click',

  // === field / dataset ===
  SET_FIELD_COMPONENT: 'setFieldComponent',
  SWITCH_DATASET: 'switchDataset',

  // === colormap (legacy globals — still emitted during the Stage 2 transition) ===
  SET_COLORMAP: 'setColormap',
  SET_COLORMAP_RANGE: 'setColormapRange',
  SET_COLORMAP_UNITS: 'setColormapUnits',
  SET_CENTER_ON_ZERO: 'setCenterOnZero',
  SET_COLORMAP_LOG: 'setColormapLog',
  REFRESH_COLORMAP: 'refreshColormap',

  // === colormap bindings (Stage 2) ===
  COLORMAP_CREATE: 'colormap:create',
  COLORMAP_UPDATE: 'colormap:update',
  COLORMAP_REMOVE: 'colormap:remove',
  COLORMAP_SHARE: 'colormap:share',
  COLORMAP_SET_VISIBLE: 'colormap:setVisible',

  // === selections registry (Stage 5) ===
  // Box + Sphere live first-class in `params.selections`. Per-primitive
  // `<x>Domain` references the registry; 'full' is the no-scope sentinel.
  SELECTION_CREATE_BOX:    'selection:create-box',
  SELECTION_CREATE_SPHERE: 'selection:create-sphere',
  SELECTION_UPDATE:        'selection:update',
  SELECTION_DELETE:        'selection:delete',
  SELECTION_FOCUS:         'selection:focus',
  SELECTION_SET_DOMAIN:    'selection:set-domain',

  // === reductions registry (Stage 6) ===
  // 0-D scalar / 1-D profile extraction. Compute fires on create / update;
  // results live on the viewer context cache, not in the store.
  REDUCTION_CREATE: 'reduction:create',
  REDUCTION_UPDATE: 'reduction:update',
  REDUCTION_DELETE: 'reduction:delete',
  REDUCTION_FOCUS:  'reduction:focus',

  // === slices ===
  SET_SLICE_POSITION: 'setSlicePosition',
  SET_SLICE_VISIBILITY: 'setSliceVisibility',
  SET_SLICE_OPACITY: 'setSliceOpacity',
  SET_SLICE_PIXELATED: 'setSlicePixelated',
  SET_TRANSPARENT_BOUNDS: 'setTransparentBounds',

  // === volume / contour / fieldlines ===
  SET_CONTOUR_VALUE: 'setContourValue',
  SET_CONTOUR_OPACITY: 'setContourOpacity',
  SET_CONTOUR_RENDER_ORDER: 'setContourRenderOrder',
  GENERATE_CONTOUR: 'generateContour',
  CLEAR_CONTOURS: 'clearContours',
  SET_CONTOUR_VISIBILITY: 'setContourVisibility',
  TOGGLE_VOLUME: 'toggleVolume',
  TRACE_FIELDLINE: 'traceFieldline',
  CLEAR_FIELDLINES: 'clearFieldlines',
  SET_FIELDLINE_DISPLAY: 'setFieldlineDisplay',
  SET_FIELDLINES_VISIBILITY: 'setFieldlinesVisibility',

  // === grid / theme ===
  SET_SHOW_GRID: 'setShowGrid',
  CYCLE_THEME: 'cycleTheme',
  LOAD_THEME: 'loadTheme',

  // === scene-graph rebuild ===
  // High-level invalidations dispatched by orchestrators (datasetManager,
  // theme handler, sceneIO). Their handlers in src/app/intents/scene.ts
  // delegate to the corresponding @scene/* builders.
  SCENE_REBUILD_SLICES: 'scene:rebuildSlices',
  SCENE_SYNC_VOLUME: 'scene:syncVolume',
  SCENE_APPLY_AXIS_GRID: 'scene:applyAxisGrid',
  // Layers-panel scene-object visibility (handlers in app/intents/scene.ts).
  SET_AXES_VECTORS_VISIBILITY: 'setAxesVectorsVisibility',
  SET_BODY_VISIBILITY: 'setBodyVisibility',

  // === ui-side ===
  UI_LOADING_SHOW: 'ui:loading:show',
  UI_LOADING_HIDE: 'ui:loading:hide',
  UI_LOADING_PROGRESS: 'ui:loading:progress',
  UI_TASK_SHOW: 'ui:task:show',
  UI_TASK_HIDE: 'ui:task:hide',
  UI_PANEL_TOGGLE: 'ui:panels:toggle',
  UI_PANEL_CYCLE: 'ui:panels:cycleVisibility',
  UI_HOVER_SHOW: 'ui:hoverTooltip:show',
  UI_HOVER_HIDE: 'ui:hoverTooltip:hide',
  UI_HOVER_REFLOW: 'ui:hoverTooltip:reflow',
  UI_PANE_REFRESH: 'ui:pane:refresh',
  UI_PANE_REBUILD_SECTION: 'ui:pane:rebuildSection',
  UI_THEME_APPLY: 'ui:theme:apply',
  UI_CHROME_HIDE: 'ui:chrome:hide',
  UI_CHROME_RESTORE: 'ui:chrome:restore',
  UI_SCREENSHOT_SAVE: 'ui:screenshot:save',
  UI_TAB_OPEN: 'ui:tab:open',
  UI_TAB_CHANGED: 'ui:tab:changed',

  // === command palette (Stage 7) ===
  // PALETTE_OPEN is dispatched by the shortcut registry on ⌘K / Ctrl+K;
  // the palette driver owns the modal lifecycle. PALETTE_CLOSE is also
  // dispatched by the chrome hide flow during screenshot capture.
  PALETTE_OPEN: 'palette:open',
  PALETTE_CLOSE: 'palette:close',

  // === timestep scrubber ===
  // Wired today but inert: single-snapshot datasets keep `availableSteps`
  // at length 1, so prev/next clamp to 0. Future readers populate the
  // array and the reduction `timestepPolicy` starts firing.
  STEP_SET:  'timestep:set',
  STEP_PREV: 'timestep:prev',
  STEP_NEXT: 'timestep:next',

  // === camera presets ===
  CAMERA_PRESET: 'camera:preset',
  // Pan-to-center the orbit pivot on a physical point (double-click focus).
  CAMERA_FOCUS_POINT: 'camera:focusPoint',
  // Swap the scene camera projection (perspective ↔ orthographic).
  CAMERA_SET_PROJECTION: 'camera:setProjection',
  // Set / cycle the scene navigation mode (orbit / turntable / fly).
  CAMERA_SET_NAV_MODE: 'camera:setNavMode',
  CAMERA_CYCLE_NAV_MODE: 'camera:cycleNavMode',

  // === colorbar units toggle ===
  SET_COLORBAR_UNITS: 'setColorbarUnits',
} as const;

export type IntentName = (typeof INTENTS)[keyof typeof INTENTS];

export type SliceAxis = 'XY' | 'ZY' | 'ZX';
export type GridPlane = 'XY' | 'XZ' | 'YZ';
/** Keys accepted by UI_PANE_REBUILD_SECTION: the six buildable sections (one
 *  pane folder each) plus 'data' (the top-bar owns it — a no-op for the
 *  pane) and 'selection-focus' (a DOM-only light path). `@ui` derives its
 *  buildable-section registry from this, so the vocabulary has one source of
 *  truth and a typo at any dispatch site is a compile error. */
export type SectionRebuildKey =
  | 'selection' | 'slices' | 'contour' | 'volume' | 'fieldlines' | 'reductions'
  | 'data' | 'selection-focus';

/**
 * Payload shape for each intent. Entries are inferred from the legacy
 * runtime guards in src/app/intents.js — if a handler accepts a missing
 * payload entirely, mark it `void`.
 */
export interface IntentPayloadMap {
  // selection
  moveSelection: { x: number; y: number; z: number };
  setSelectionPosition: { x: number; y: number; z: number };
  setSelectionSize: { value: number };
  setSelectionVisibility: { visible: boolean };
  setSelectionOverlayVisibility: { visible: boolean };
  'selection:probe-click': void;

  // field / dataset
  setFieldComponent: { component: string };
  switchDataset: { key: string };

  // colormap (legacy globals)
  setColormap: { name: string };
  setColormapRange: { min?: number; max?: number };
  setColormapUnits: { units: string };
  setCenterOnZero: { value: boolean };
  setColormapLog: { value: boolean };
  refreshColormap: void;

  // colormap bindings (Stage 2)
  'colormap:create': {
    field: string;
    /** Initial values; min/max/units fall back to sensible defaults when absent. */
    seed?: Partial<Omit<ColormapBinding, 'id'>>;
    /** Primitives whose `*ColormapId` should be repointed at the new binding. */
    bindTo?: ReadonlyArray<PrimitiveKey>;
  };
  'colormap:update': { id: ColormapBindingId; patch: Partial<Omit<ColormapBinding, 'id'>> };
  'colormap:remove': { id: ColormapBindingId };
  'colormap:share': { primitive: PrimitiveKey; targetId: ColormapBindingId };
  'colormap:setVisible': { ids: ReadonlyArray<ColormapBindingId> };

  // selections registry (Stage 5)
  'selection:create-box': {
    /** Initial bounds (physical coords). Defaults to 25-75% of grid. */
    lower?: readonly [number, number, number];
    upper?: readonly [number, number, number];
    label?: string;
    /** Focus the new selection on create (default true). */
    focus?: boolean;
  };
  'selection:create-sphere': {
    center?: readonly [number, number, number];
    radius?: number;
    keep?: 'inside' | 'outside';
    label?: string;
    focus?: boolean;
  };
  'selection:update': {
    id: SelectionId;
    patch: import('./selections').SelectionPatch;
  };
  'selection:delete': { id: SelectionId };
  'selection:focus': { id: SelectionId | null };
  'selection:set-domain': { primitive: PrimitiveKey; selectionId: SelectionId | 'full' };

  // reductions registry (Stage 6)
  'reduction:create': {
    /** Initial values; `domain` defaults to 'full', `timestepPolicy` to 'follow'. */
    seed?: Partial<Omit<Reduction, 'id'>>;
    /** Focus the new reduction on create (default true). */
    focus?: boolean;
  };
  'reduction:update': { id: ReductionId; patch: Partial<Omit<Reduction, 'id'>> };
  'reduction:delete': { id: ReductionId };
  'reduction:focus': { id: ReductionId | null };

  // slices
  setSlicePosition: { axis: SliceAxis; value: number };
  setSliceVisibility: { axis: SliceAxis; visible: boolean };
  setSliceOpacity: { value: number };
  setSlicePixelated: { value: boolean };
  setTransparentBounds: { value: boolean };

  // volume / contour / fieldlines
  setContourValue: { value: number };
  setContourOpacity: { value: number };
  setContourRenderOrder: { value: number };
  generateContour: void;
  clearContours: void;
  setContourVisibility: { visible: boolean };
  toggleVolume: { enabled?: boolean } | void;
  traceFieldline: void;
  clearFieldlines: void;
  setFieldlineDisplay: { rebuildGeometry?: boolean } | void;
  setFieldlinesVisibility: { visible: boolean };

  // grid / theme
  setShowGrid: { plane: GridPlane; value: boolean };
  cycleTheme: void;
  loadTheme: { url: string };

  // scene-graph rebuild
  'scene:rebuildSlices': void;
  'scene:syncVolume': void;
  'scene:applyAxisGrid': void;
  setAxesVectorsVisibility: { visible: boolean };
  setBodyVisibility: { id: string; visible: boolean };

  // ui-side
  'ui:loading:show': { msg?: string };
  'ui:loading:hide': void;
  'ui:loading:progress': { value: number };
  'ui:task:show': { msg?: string };
  'ui:task:hide': void;
  'ui:panels:toggle': { which: 'controls' | 'colorbar' } | void;
  'ui:panels:cycleVisibility': void;
  'ui:hoverTooltip:show': {
    screenX: number;
    screenY: number;
    pinned?: boolean;
    sample: {
      posPhys: { x: number; y: number; z: number };
      value: number;
      fieldLabel: string;
      coordUnits: string;
      fieldUnits: string;
    };
  };
  'ui:hoverTooltip:hide': void;
  // Reposition-only: move the panel to follow the re-projected marker as the
  // camera orbits, without touching its show/hide lifecycle. No-op if hidden.
  'ui:hoverTooltip:reflow': { screenX: number; screenY: number };
  'ui:pane:refresh': void;
  'ui:pane:rebuildSection': { which?: SectionRebuildKey | readonly SectionRebuildKey[]; prevId?: SelectionId | null; newId?: SelectionId | null } | void;
  'ui:theme:apply': { theme: import('../config/defaults').ThemeDefaults };
  'ui:chrome:hide': void;
  'ui:chrome:restore': void;
  'ui:screenshot:save': void;
  'ui:tab:open': { which: 'selection' | 'slices' | 'contour' | 'volume' | 'fieldlines' | 'vectors' | 'particles' | 'reductions' };
  // Fired whenever the active rail section changes (open, switch, or close →
  // null). The single source is `setOpen()` in the tabs builder.
  'ui:tab:changed': { which: string | null };

  // command palette (Stage 7)
  'palette:open': void;
  'palette:close': void;

  // timestep scrubber — inert until availableSteps grows past 1
  'timestep:set':  { step: number };
  'timestep:prev': void;
  'timestep:next': void;

  // camera presets
  'camera:preset': { preset: CameraPreset };
  'camera:focusPoint': { x: number; y: number; z: number };
  'camera:setProjection': { mode: CameraProjection };
  'camera:setNavMode': { mode: NavMode };
  'camera:cycleNavMode': void;

  // colorbar units toggle
  setColorbarUnits: { mode: ColorbarUnitsMode };
}

/** Compile-time guard: every IntentName has a payload entry. */
export type _IntentNameMatchesMap = IntentName extends keyof IntentPayloadMap ? true : never;
export type _IntentMapMatchesNames = keyof IntentPayloadMap extends Vec3Obj | IntentName ? true : never;
